import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronDown, Clock, Video as VideoIcon, X, BookOpen } from 'lucide-react';
import { getSkillAssessmentVideo } from '../../../../../../../../utils/api_service/assessments_api';
import { getVideoFromStorage } from '../../../../../../../../utils/api_service/storage_api';
import { IQuestionAndAnswer } from '../Interfaces';

export const QuestionAndAnswer: React.FC<IQuestionAndAnswer> = (props) => {
  const {
    handleOpenQuestion,
    question,
    videoResponse,
    score = 0,
    timeTaken,
    skillAttribute,
    openQuestion,
    response,
    followUpQuestions,
    questionId,
  } = props;

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);

  const isOpen = openQuestion === question;

  // Function to fetch video from storage
  const fetchVideo = async (videoId: string) => {
    if (!videoId) return;

    setVideoLoading(true);
    setVideoError(null);

    try {
      let url: string;
      if (props.isSkillAssessment && typeof videoId === 'string') {
        url = await getSkillAssessmentVideo(videoId);
      } else {
        url = await getVideoFromStorage(videoId);
      }
      setVideoUrl(url);
    } catch {
      setVideoError('Failed to load video');
    } finally {
      setVideoLoading(false);
    }
  };

  // Handle opening video modal
  const handleOpenVideoModal = () => {
    setIsVideoModalOpen(true);
    if (typeof videoResponse === 'string') {
      if (props.isSkillAssessment) {
        fetchVideo(videoResponse);
      } else if (
        videoResponse.includes('/') ||
        videoResponse.includes('.webm') ||
        videoResponse.includes('.mp4')
      ) {
        fetchVideo(videoResponse);
      } else {
        setVideoUrl(videoResponse);
      }
    } else {
      setVideoUrl(null);
      setVideoError('No video available');
    }
  };

  // Handle closing video modal
  const handleCloseVideoModal = () => {
    setIsVideoModalOpen(false);
    setVideoUrl(null);
    setVideoError(null);
    setVideoLoading(false);
  };

  const getScoreColor = (num: number) => {
    if (num >= 85) return 'success.main';
    if (num >= 70) return 'primary.main';
    return 'warning.main';
  };

  return (
    <>
      {/* Video Modal */}
      <Dialog
        open={isVideoModalOpen}
        onClose={handleCloseVideoModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                bgcolor: 'primary.50',
                border: 1,
                borderColor: 'primary.100',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <VideoIcon size={18} color="#2563eb" />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                Video Response
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {question}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleCloseVideoModal} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            {/* Left Column: Video */}
            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: '#0f172a',
                  borderRadius: 2,
                  overflow: 'hidden',
                  aspectRatio: '16/9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                {videoLoading ? (
                  <Stack alignItems="center" spacing={2} color="#94a3b8">
                    <CircularProgress size={40} color="primary" />
                    <Typography variant="body1" fontWeight={500}>
                      Loading Video...
                    </Typography>
                    <Typography variant="caption">
                      Please wait while we fetch your video.
                    </Typography>
                  </Stack>
                ) : videoError ? (
                  <Stack alignItems="center" spacing={1.5} color="#f87171">
                    <VideoIcon size={40} />
                    <Typography variant="body1" fontWeight={600}>
                      Error Loading Video
                    </Typography>
                    <Typography variant="caption">{videoError}</Typography>
                  </Stack>
                ) : videoUrl ? (
                  <Box
                    component="video"
                    controls
                    src={videoUrl}
                    preload="metadata"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Stack alignItems="center" spacing={1} color="#94a3b8">
                    <VideoIcon size={40} />
                    <Typography variant="body2" fontWeight={500}>
                      Video Not Available
                    </Typography>
                  </Stack>
                )}
              </Paper>
            </Grid>

            {/* Right Column: Metadata & Transcript */}
            <Grid item xs={12} md={5}>
              <Stack spacing={2} sx={{ height: '100%' }}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4} md={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        borderRadius: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                        textTransform="uppercase"
                      >
                        Score
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight={700}
                        color={getScoreColor(score)}
                      >
                        {score}%
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={4} md={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        borderRadius: 1.5,
                      }}
                    >
                      <Stack direction="row" spacing={0.75} alignItems="center" mb={0.5}>
                        <Clock size={14} color="#64748b" />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          textTransform="uppercase"
                        >
                          Duration
                        </Typography>
                      </Stack>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {timeTaken}
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={4} md={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.default',
                        borderRadius: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                        textTransform="uppercase"
                      >
                        Skill
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ textTransform: 'uppercase', mt: 0.5 }}
                        noWrap
                      >
                        {skillAttribute}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {response && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'background.default',
                      borderRadius: 1.5,
                      flex: 1,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                      mb={1}
                    >
                      Transcript
                    </Typography>
                    <Box
                      sx={{
                        overflowY: 'auto',
                        maxHeight: 200,
                        pr: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                      >
                        {response}
                      </Typography>
                    </Box>
                  </Paper>
                )}
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Main Question Item */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          transition: 'all 0.3s ease',
          bgcolor: isOpen ? 'action.hover' : 'background.paper',
        }}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <ButtonBase
            onClick={() => handleOpenQuestion(question)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              textAlign: 'left',
              gap: 2,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" fontWeight={500} color="text.primary">
                {question}
              </Typography>
              {videoResponse && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VideoIcon size={12} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenVideoModal();
                  }}
                  sx={{
                    mt: 1,
                    py: 0.25,
                    px: 1,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Video Response
                </Button>
              )}
            </Box>

            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ minWidth: { xs: 'auto', sm: 260 } }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ color: 'primary.main', cursor: 'pointer' }}
              >
                <Typography variant="caption" fontWeight={600}>
                  {isOpen ? 'Hide transcript' : 'Click to see transcript'}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    transform: isOpen ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.2s ease',
                  }}
                >
                  <ChevronDown size={16} />
                </Box>
              </Stack>

              <Chip
                label={skillAttribute}
                size="small"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  bgcolor: 'background.paper',
                }}
              />
            </Stack>
          </ButtonBase>

          <Collapse in={isOpen}>
            <Box sx={{ mt: 2.5 }}>
              {/* Response Section */}
              {response && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 0.5,
                        bgcolor: 'primary.50',
                        border: 1,
                        borderColor: 'primary.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {videoResponse ? (
                        <VideoIcon size={12} color="#2563eb" />
                      ) : (
                        <Typography
                          variant="caption"
                          fontWeight={800}
                          color="primary.main"
                          fontSize="0.65rem"
                        >
                          T
                        </Typography>
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      textTransform="uppercase"
                      letterSpacing="0.05em"
                    >
                      Response Transcript
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
                  >
                    {response}
                  </Typography>
                </Paper>
              )}

              {/* Follow-up Questions */}
              {followUpQuestions && followUpQuestions.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: 0.5,
                        bgcolor: 'secondary.50',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <BookOpen size={13} color="#9333ea" />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Response Analysis
                    </Typography>
                  </Stack>
                  <Stack spacing={1.5}>
                    {followUpQuestions.map((fq, idx) => (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 2,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1.5,
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: 'grey.300',
                            boxShadow: 1,
                          },
                        }}
                      >
                        <Typography variant="body2" color="text.primary" mb={1}>
                          {fq}
                        </Typography>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', opacity: 0.7 }}
                        >
                          {idx === 0 &&
                            'Probes situational awareness and problem recognition'}
                          {idx === 1 &&
                            'Assesses emotional regulation and initial response mechanisms'}
                          {idx === 2 &&
                            'Evaluates critical thinking and decision-making process'}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </>
  );
};
